import { BreedOptionType } from '@modernanimal/common/src/utils/patientOnboarding';
import { StateCreator } from 'zustand';
import { devtools, persist, createJSONStorage } from 'zustand/middleware';
import { createWithEqualityFn } from 'zustand/traditional';

import {
    CustomerPatientSexEnum, CustomerPatientSpeciesEnum,
    GetColorsQuery, CreateAppointmentMutation, CustomerMembershipTierEnum,
    CustomerPatientRecordsAvailabilityEnum,
    HouseholdType,
} from './__generated__/graphql';
import { Slot } from './AppointmentSelection';
import { Promo } from './clientOnboarding/PromoCodeInput';

/* Types */

export type AppointmentStoreType = {
    slot: Slot | null;
    reasonForVisitDescription: string;
    createdAppointment?: CreateAppointmentMutation['createAppointment'];
};

type AppointmentSlice = {
    appointment: AppointmentStoreType;
    setAppointment: (appointment: AppointmentStoreType)=> void;
};

type ClientHouseholdType = {
    id: string,
    signupLocationId: string,
    timezone: string
}

export type ClientStoreType = {
    id: string;
    token: string;
    phoneNumber: string;
    household: ClientHouseholdType;
    firstName: string;
    lastName: string;
    email: string;
    fullAddress: {
        address: string;
        apartment: string;
        city?: string | null;
        state?: string | null;
        zipcode?: string | null;
        text: google.maps.places.FormattableText;
        placeId: string;
    };
};

type ClientSlice = {
    client: ClientStoreType;
    setClient: (client: ClientStoreType)=> void;
};

export type PatientStoreType = {
    id: string;
    name: string;
    species: CustomerPatientSpeciesEnum | null;
    birthdate: string;
    isBirthdateKnown: boolean | null;
    breed: BreedOptionType;
    color: GetColorsQuery['colors'][0];
    sex: CustomerPatientSexEnum | null;
    spayedOrNeutered: boolean | null;
    membershipTier: CustomerMembershipTierEnum;
    promo: Promo | null;
    recordsAvailability: CustomerPatientRecordsAvailabilityEnum | null;
    referringHousehold: Pick<HouseholdType, 'id' | 'householdHash'> | null;
};

type PatientSlice = {
    patient: PatientStoreType;
    setPatient: (patient: PatientStoreType)=> void;
};

export type ExternalClinicStoreType = {
    name: string;
    formattedAddress: string;
    placeId: string;
    phoneNumber: string;
    cityText: google.maps.places.FormattableText;
    cityPlaceId: string;
};

type AdditionalInfoSlice = {
    onGoToPrevStep?: ()=> void;
    setOnGoToPrevStep: (onGoToPrevStep?: ()=> void)=> void;
    externalClinic: ExternalClinicStoreType;
    setExternalClinic: (externalClinic: ExternalClinicStoreType)=> void;
    isCreditCardCreated: boolean;
    setIsCreditCardCreated: (isCreditCardCreated: boolean)=> void;
    isMedicalRecordRequested: boolean;
    setIsMedialRecordsRequested: (isMedicalRecordRequested: boolean)=> void;
    isMembershipCreated: boolean;
    setIsMembershipCreated: (isMembershipCreated: boolean)=> void;
};

type WebBookingSlices = AppointmentSlice & ClientSlice & PatientSlice & AdditionalInfoSlice;

/* Initial States */

const initialAppointmentState: AppointmentStoreType = {
    slot: null,
    reasonForVisitDescription: '',
};

export const initialClientState: ClientStoreType = {
    id: '',
    token: '',
    phoneNumber: '',
    household: {
        id: '',
        signupLocationId: '',
        timezone: ''
    },
    firstName: '',
    lastName: '',
    email: '',
    fullAddress: {
        address: '',
        apartment: '',
        city: '',
        state: '',
        zipcode: '',
        text: {
            matches: [],
            text: ''
        },
        placeId: ''
    }
};

const initialPatientState: PatientStoreType = {
    id: '',
    name: '',
    species: null,
    birthdate: '',
    isBirthdateKnown: null,
    breed: {
        id: '',
        name: '',
        isMixed: false
    },
    color: {
        id: '',
        name: ''
    },
    sex: null,
    spayedOrNeutered: null,
    membershipTier: CustomerMembershipTierEnum.PayAsYouGo,
    promo: null,
    recordsAvailability: null,
    referringHousehold: null
};

export const initialExternalClinicState: ExternalClinicStoreType = {
    name: '',
    formattedAddress: '',
    placeId: '',
    phoneNumber: '',
    cityText: {
        matches: [],
        text: ''
    },
    cityPlaceId: ''
};

/* Slices */

// const resetters: (()=> void)[] = [];
const resetters: Partial<Record<keyof WebBookingSlices, ()=> void>> = {};

const createAppointmentSlice: StateCreator<WebBookingSlices, [], [], AppointmentSlice> = (set) => {
    resetters.appointment = () => set({ appointment: initialAppointmentState });
    return ({
        appointment: initialAppointmentState,
        setAppointment: (appointment) => set({ appointment })
    });
};

const createClientSlice: StateCreator<WebBookingSlices, [], [], ClientSlice> = (set) => {
    resetters.client = () => set({ client: initialClientState });
    return ({
        client: initialClientState,
        setClient: (client) => set({ client })
    });
};

const createPatientSlice: StateCreator<WebBookingSlices, [], [], PatientSlice> = (set) => {
    resetters.patient = () => set({ patient: initialPatientState });
    return ({
        patient: initialPatientState,
        setPatient: (patient) => set({ patient })
    });
};

const createAdditionalInfoSlice: StateCreator<WebBookingSlices, [], [], AdditionalInfoSlice> = (set) => {
    resetters.externalClinic = () => set({ externalClinic: initialExternalClinicState });
    resetters.onGoToPrevStep = () => set({ onGoToPrevStep: undefined });
    resetters.isCreditCardCreated = () => set({ isCreditCardCreated: false });
    resetters.isMedicalRecordRequested = () => set({ isMedicalRecordRequested: false });
    resetters.isMembershipCreated = () => set({ isMembershipCreated: false });
    return ({
        onGoToPrevStep: undefined,
        setOnGoToPrevStep: (onGoToPrevStep) => set({ onGoToPrevStep }),
        externalClinic: initialExternalClinicState,
        setExternalClinic: (externalClinic) => set({ externalClinic }),
        isCreditCardCreated: false,
        setIsCreditCardCreated: (isCreditCardCreated) => set({ isCreditCardCreated }),
        isMedicalRecordRequested: false,
        setIsMedialRecordsRequested: (isMedicalRecordRequested) => set({ isMedicalRecordRequested }),
        isMembershipCreated: false,
        setIsMembershipCreated: (isMembershipCreated) => set({ isMembershipCreated }),
    });
};

/* Web Booking Store */

export const LOCAL_STORAGE_KEY = 'web-booking-storage';

const useWebBookingStore = createWithEqualityFn<WebBookingSlices>()(
    devtools(
        persist(
            (...a) => ({
                ...createAppointmentSlice(...a),
                ...createClientSlice(...a),
                ...createPatientSlice(...a),
                ...createAdditionalInfoSlice(...a)
            }),
            {
                name: LOCAL_STORAGE_KEY,
                storage: createJSONStorage(() => localStorage),
                // Prevent certain keys from being persisted to local storage
                partialize: (state) => {
                    const updatedState = Object.fromEntries(Object.entries(state).filter(([key]) => key !== 'onGoToPrevStep'));
                    const updatedClient = Object.fromEntries(Object.entries(updatedState.client).filter(([key]) => key !== 'token'));
                    const updatedAppointment = Object.fromEntries(Object.entries(updatedState.appointment).filter(([key]) => key !== 'createdAppointment'));
                    return ({
                        ...updatedState,
                        client: updatedClient,
                        appointment: updatedAppointment
                    });
                },
                // Change version number when introducing breaking changes in storage (e.g. renaming a field).
                // https://docs.pmnd.rs/zustand/integrations/persisting-store-data#version
                // Please use yyyymm<<version_number>> format for more informative versioning.
                version: 20230903
            }
        )
    ),
    Object.is
);

export const resetAllSlices = (
    excludedSlices: (keyof WebBookingSlices)[] = []
) => Object.entries(resetters).forEach(([key, resetter]) => {
    if (!excludedSlices.includes(key as keyof WebBookingSlices)) {
        resetter?.();
    }
});

export default useWebBookingStore;
